import 'keen-slider/keen-slider.min.css';
import { useRef, useState } from 'react';
import { Icon, IconID } from '@common/Icon/Icon';
import { Modal } from '@common/Modal/Modal';
import { WidenImage } from '@common/Widen/WidenImage';
import { WidenVideo } from '@common/Widen/WidenVideo';
import { useIsomorphicLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import type { TechnicalData } from '@models/data';
import { Badge } from '@common/Badge/Badge';
import { useTranslation } from 'next-i18next';
// eslint-disable-next-line import/no-cycle
import { TechnicalTable } from '@product/TechnicalTable/TechnicalTable';
import type { WidenMediaElement } from '@libs/widen-utils';
import { MediaElementType } from '@libs/widen-utils';
import { getAttributeValue } from '@libs/attribute.helper';
import { ProductHelper } from '@models/graphql';
import { useProductPageStore } from '@hooks/states/useProductPageStore';

export interface ProductMediaTeaserModalProps {
  media: WidenMediaElement;
  drawingTechnicalData?: TechnicalData;
}

export const ProductMediaTeaserModal = ({ media, drawingTechnicalData }: ProductMediaTeaserModalProps) => {
  const { t } = useTranslation(['product/productDetails', 'product/productMediaCarousel']);

  const currentProduct = useProductPageStore(state => state.currentProduct);
  const isMountedRef = useRef<boolean>(false);
  const isProductDiscontinued = ProductHelper.isDiscontinued(currentProduct);
  const isProductNew = ProductHelper.isNew(currentProduct);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const headline = getAttributeValue<string>(media.heading);
  const { description } = media;
  const isDrawing = media.type === MediaElementType.DRAWING;

  return (
    <>
      {isProductNew && <Badge text={t('PRODUCT_DETAILS.NEW_PRODUCT_BADGE')} />}
      {isProductDiscontinued && <Badge text={t('PRODUCT_DETAILS.DISCONTINUED_BADGE')} />}
      <h4 className="w-full top-0 h-16 flex flex-col justify-center">{headline}</h4>
      <div data-testid="product-image-teaser-modal" className="relative m-auto mt-8" style={{ maxWidth: '1024px' }}>
        <div className="relative border border-gray-200 m-auto">
          {media.type === MediaElementType.IMAGE || isDrawing ? (
            <WidenImage
              width={1024}
              height={680}
              data-testid="product-image-teaser"
              alt={media.description || media.title}
              layout="intrinsic"
              shortCode={media.shortCode}
              filename={media.filename}
              title={media.title}
            />
          ) : (
            <WidenVideo
              data-testid="product-video-teaser"
              shortCode={media.shortCode}
              filename={media.filename}
              posterWidth={640}
              posterHeight={480}
              hd
              title={media.title}
            />
          )}
        </div>
        <div className="text-left">
          {description && !isDrawing && (
            <p
              data-testid="product-media-teaser-description"
              className="mt-6"
              dangerouslySetInnerHTML={{ __html: description || '' }}
            />
          )}
          {!isDrawing && (
            <p data-testid="product-media-teaser-similar-info" className="text-xxs mt-6">
              {t('PRODUCT_MEDIA_CAROUSEL.SIMILAR', { ns: 'product/productMediaCarousel' })}
            </p>
          )}
          {isDrawing && drawingTechnicalData && drawingTechnicalData && (
            <div className="mt-16" data-testid="product-media-teaser-drawing-technical-data">
              <TechnicalTable data={[drawingTechnicalData]} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export interface ProductMediaTeaserProps {
  media: WidenMediaElement;
  width: number;
  height: number;
  drawingTechnicalData?: TechnicalData;
  objectFit?: 'contain' | 'fill' | 'cover' | 'scale-down' | 'none';
}

export const ProductMediaTeaser = ({
  media,
  width,
  height,
  drawingTechnicalData,
  objectFit,
}: ProductMediaTeaserProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="relative text-center max-w-full" data-testid="product-media-teaser">
      <div className="cursor-pointer border border-gray-200 relative" onClick={() => setShowModal(true)}>
        <WidenImage
          width={width}
          height={height}
          alt={media?.description || media?.title || ''}
          shortCode={media?.shortCode}
          filename={media?.filename}
          layout="responsive"
          title={media?.title}
          objectFit={objectFit}
        />
        <div className="absolute bottom-4 right-4">
          <Icon size={10} id={IconID.MAGNIFIER} primary={false} />
        </div>
      </div>

      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <ProductMediaTeaserModal media={media} drawingTechnicalData={drawingTechnicalData} />
      </Modal>
    </div>
  );
};
